import { clientLogger } from '@/lib/clientLogger';
/**
 * SmsConversationPanel
 *
 * Reusable chat thread panel: message bubbles (with full MMS media rendering),
 * file attachment upload, and composer. Extracted from VoipMessaging so it can
 * be shared with JobContactConversationModal and any future contexts.
 *
 * The parent is responsible for:
 *  - Providing the messages list (any order — panel sorts oldest-first)
 *  - Making the actual sendMessageV2 API call (via onSend callback)
 *  - Tracking sending / sendError state
 *  - Realtime subscriptions and optimistic message injection
 *
 * The panel is responsible for:
 *  - Rendering messages with media
 *  - File selection, S3 pre-signed upload, pending file previews
 *  - Calling onSend(body, attachments) — parent gets attachment info to build
 *    optimistic media before calling the API
 *  - Image preview modal + download
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
    Send, Paperclip, X, Download, FileText,
    Image, AlertCircle, Loader2, ExternalLink, ArrowUpRight,
} from 'lucide-react';
import { apiService } from '@/lib/api';
import { MessageDTOv2 } from '@/types';
import { ImagePreviewModal } from './ui/ImagePreviewModal';
import { openDownloadUrl, isNative } from '@/lib/capacitor';
import { detectIsMobile } from '@/hooks/useIsMobile';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatMessageTime(dateStr: string): string {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const dDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((dNow.getTime() - dDate.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function groupMessagesByDate(messages: MessageDTOv2[]): { label: string; messages: MessageDTOv2[] }[] {
    // Sort oldest-first for display regardless of input order
    const sorted = [...messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const groups: { label: string; messages: MessageDTOv2[] }[] = [];
    for (const msg of sorted) {
        const label = formatDateLabel(msg.createdAt);
        const last = groups[groups.length - 1];
        if (last && last.label === label) {
            last.messages.push(msg);
        } else {
            groups.push({ label, messages: [msg] });
        }
    }
    return groups;
}

// ── Public types ──────────────────────────────────────────────────────────────

export interface SmsAttachment {
    objectKey: string;
    contentType: string;
    /** Blob preview URL for the optimistic media item (images only) */
    previewUrl?: string;
}

export type PendingSmsFile = SmsAttachment & { name: string; size: number };

export const MMS_MAX_FILES = 10;
export const MMS_ACCEPT = 'image/*,video/*,audio/*,application/pdf';

/** Presign + POST each file to S3. Caller owns pending-file state and URL.revokeObjectURL. */
export async function uploadMmsFiles(files: File[]): Promise<PendingSmsFile[]> {
    const items = await apiService.prepareMmsMediaUpload(files.map(f => f.name));
    const uploaded: PendingSmsFile[] = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const file = files[i];
        const formData = new FormData();
        Object.entries(item.fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);
        const res = await fetch(item.uploadUrl, { method: 'POST', body: formData });
        if (!res.ok) throw new Error(`Upload failed for ${file.name}: ${res.status}`);
        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
        uploaded.push({ name: file.name, size: file.size, objectKey: item.objectKey, contentType: item.contentType, previewUrl });
    }
    return uploaded;
}

export function revokeSmsPreviewUrls(files: { previewUrl?: string }[]) {
    files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
}

export interface SmsConversationPanelProps {
    messages: MessageDTOv2[];
    /** Show a full-height loading spinner (messages not yet loaded) */
    loading?: boolean;
    /** Spinner on the send button */
    sending?: boolean;
    /** Error banner below composer */
    sendError?: string | null;
    onClearSendError?: () => void;
    /**
     * Called when the user hits Send.  
     * The panel has already cleared its local messageInput + pendingFiles before calling.  
     * `attachments` contains the already-uploaded S3 object keys + content types so the
     * parent can build an optimistic MessageDTOv2.media array before calling sendMessageV2.
     */
    onSend: (body: string, attachments?: SmsAttachment[]) => Promise<void>;
    /** Disable the composer (e.g. while resolve is pending) */
    disabled?: boolean;
    /** Placeholder shown while `disabled`. Defaults to "Loading…", which is wrong when the
     *  composer is disabled for a lasting reason (an inactive or released sending number). */
    disabledPlaceholder?: string;
    /** Show the paperclip / file-attach button (Twilio numbers only) */
    supportsMms?: boolean;
    /** Show a "Load earlier messages" button at the top */
    hasMore?: boolean;
    onLoadMore?: () => void;
    loadingMore?: boolean;
    placeholder?: string;
    /**
     * Optional external ref for the scroll container div.
     * Pass this when the parent needs to read/manipulate scrollHeight
     * (e.g. VoipMessaging's load-more scroll-position preservation).
     */
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    /**
     * When true, show sentByUserName on outbound messages (provider-agnostic;
     * only renders when the message actually carries a sender name).
     */
    showSenderNames?: boolean;
    /** Extra classes for the composer footer (e.g. safe-area + keyboard on native modals). */
    composerClassName?: string;
    /** When set, messages with a jobId show a button (and invoice number) that opens the job. */
    onOpenJob?: (jobId: string) => void;
    /**
     * Desktop chat shortcut: Enter sends, Shift+Enter inserts a newline.
     * Conversation modals pass false so Return matches SendSmsModal (newline).
     * Native always inserts a newline regardless — mobile keyboards have no Shift+Enter.
     */
    sendOnEnter?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SmsConversationPanel({
    messages,
    loading,
    sending,
    sendError,
    onClearSendError,
    onSend,
    disabled,
    disabledPlaceholder,
    supportsMms,
    hasMore,
    onLoadMore,
    loadingMore,
    placeholder = 'Type a message…',
    scrollContainerRef,
    showSenderNames,
    composerClassName,
    onOpenJob,
    sendOnEnter = true,
}: SmsConversationPanelProps) {
    const [messageInput, setMessageInput] = useState('');
    const [pendingFiles, setPendingFiles] = useState<PendingSmsFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const [isDownloadingMedia, setIsDownloadingMedia] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const ownScrollRef = useRef<HTMLDivElement>(null);
    const messageContentRef = useRef<HTMLDivElement>(null);
    const effectiveScrollRef = scrollContainerRef ?? ownScrollRef;
    // Track newest message timestamp to auto-scroll only when a new message arrives,
    // not when older messages are prepended via load-more.
    const newestTsRef = useRef(0);
    // True while the user is at (or scrolled to) the bottom. Starts true so the
    // initial load and any image loads before the user first scrolls snap to bottom.
    const isPinnedToBottom = useRef(true);

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            const container = effectiveScrollRef.current;
            if (container) container.scrollTop = container.scrollHeight;
        });
    }, [effectiveScrollRef]);

    // Keep the pinned flag current as the user scrolls.
    const handleContainerScroll = useCallback(() => {
        const container = effectiveScrollRef.current;
        if (!container) return;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        isPinnedToBottom.current = distanceFromBottom < 50;
    }, [effectiveScrollRef]);

    useEffect(() => {
        const newestTs = messages.reduce((max, m) => {
            const t = new Date(m.createdAt).getTime();
            return t > max ? t : max;
        }, 0);
        if (newestTs > newestTsRef.current) {
            newestTsRef.current = newestTs;
            isPinnedToBottom.current = true;
            requestAnimationFrame(() => {
                const container = effectiveScrollRef.current;
                if (container) container.scrollTop = container.scrollHeight;
            });
        }
    }, [messages, effectiveScrollRef]);

    useEffect(() => {
        const content = messageContentRef.current;
        if (!content || typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(() => {
            if (isPinnedToBottom.current) scrollToBottom();
        });

        observer.observe(content);
        return () => observer.disconnect();
    }, [loading, scrollToBottom]);

    // ── File handling ─────────────────────────────────────────────────────────

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (!files.length) return;
        e.target.value = ''; // allow re-selecting same file
        const remaining = MMS_MAX_FILES - pendingFiles.length;
        const toProcess = files.slice(0, remaining);
        if (!toProcess.length) return;

        setIsUploading(true);
        setUploadError(null);
        try {
            const uploaded = await uploadMmsFiles(toProcess);
            setPendingFiles(prev => [...prev, ...uploaded]);
        } catch (err: any) {
            setUploadError(err.message ?? 'File upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    }, [pendingFiles.length]);

    const removeFile = useCallback((objectKey: string) => {
        setPendingFiles(prev => {
            const removed = prev.find(f => f.objectKey === objectKey);
            if (removed) revokeSmsPreviewUrls([removed]);
            return prev.filter(f => f.objectKey !== objectKey);
        });
    }, []);

    // ── Media download ────────────────────────────────────────────────────────

    const handleDownloadMediaUrl = useCallback(async (url: string) => {
        try {
            if (isNative()) { await openDownloadUrl(url); return; }
            const resp = await fetch(url);
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = 'mms-media';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            clientLogger.error('Failed to download media', err);
        }
    }, []);

    const handleDownloadPreview = useCallback(async () => {
        if (!previewImageUrl || isDownloadingMedia) return;
        setIsDownloadingMedia(true);
        try {
            await handleDownloadMediaUrl(previewImageUrl);
        } finally {
            setIsDownloadingMedia(false);
        }
    }, [previewImageUrl, isDownloadingMedia, handleDownloadMediaUrl]);

    // ── Send ──────────────────────────────────────────────────────────────────

    const handleSend = useCallback(async () => {
        const hasText = messageInput.trim().length > 0;
        const hasAttachments = pendingFiles.length > 0;
        if ((!hasText && !hasAttachments) || sending || disabled) return;

        const body = messageInput.trim();
        const attachments: SmsAttachment[] = pendingFiles.map(f => ({
            objectKey: f.objectKey,
            contentType: f.contentType,
            previewUrl: f.previewUrl,
        }));

        // Clear local state immediately (optimistic feel)
        setMessageInput('');
        setPendingFiles([]);

        await onSend(body, attachments.length > 0 ? attachments : undefined);
        textareaRef.current?.focus();
    }, [messageInput, pendingFiles, sending, disabled, onSend]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Touch keyboards (native app or mobile browser) have no Shift+Enter;
        // Return must insert a newline (same as SendSmsModal). Desktop inbox
        // keeps Enter-to-send unless the parent opts out (JobContactConversationModal).
        if (!sendOnEnter || isNative() || detectIsMobile()) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const groups = groupMessagesByDate(messages);
    const canSend = (messageInput.trim().length > 0 || pendingFiles.length > 0) && !sending && !disabled && !isUploading;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Message list */}
            <div ref={effectiveScrollRef} onScroll={handleContainerScroll} className="flex-1 overflow-y-auto p-4 bg-secondary-50">
                <div ref={messageContentRef} className="min-h-full space-y-3">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-secondary-400" />
                    </div>
                ) : (
                    <>
                        {/* Load earlier messages */}
                        {hasMore && (
                            <button
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className="w-full py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {loadingMore ? 'Loading…' : 'Load earlier messages'}
                            </button>
                        )}

                        {messages.length === 0 && (
                            <div className="flex items-center justify-center h-full text-secondary-400 text-sm">
                                No messages yet. Start the conversation!
                            </div>
                        )}

                        {groups.map((group) => (
                            <div key={group.label} className="relative">
                                {/* Sticky date label */}
                                <div className="sticky top-0 z-10 flex justify-center py-3 pointer-events-none">
                                    <span className="px-4 py-1.5 bg-white/80 backdrop-blur-md shadow-sm text-secondary-600 text-[10px] font-bold rounded-full uppercase tracking-widest border border-secondary-200/50 pointer-events-auto">
                                        {group.label}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    {group.messages.map((msg) => {
                                        const isSent = msg.direction === 'outbound';
                                        const isFailed = msg.status === 'failed';
                                        const isUndelivered = msg.status === 'undelivered';
                                        const isError = isFailed || isUndelivered;
                                        const isPending = msg.status === 'sending' || msg.status === 'queued';
                                        const isSentConfirmed = msg.status === 'sent';
                                        const canOpenJob = !!(msg.jobId && onOpenJob);
                                        const jobLinkButton = canOpenJob ? (
                                            <button
                                                type="button"
                                                onClick={() => onOpenJob?.(msg.jobId!)}
                                                className="p-1.5 mb-1 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg flex-shrink-0"
                                                title={msg.jobRef ? `Open job ${msg.jobRef}` : 'Open job'}
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </button>
                                        ) : null;

                                        return (
                                            <div key={msg.id} className={`flex items-end gap-1 ${isSent ? 'justify-end' : 'justify-start'}`}>
                                                {isSent && jobLinkButton}
                                                <div className={`max-w-[75%] min-w-0 px-4 py-2.5 rounded-2xl ${
                                                    isSent
                                                        ? isError
                                                            ? 'bg-error-100 border border-error-300 text-error-900 rounded-br-md'
                                                            : 'bg-primary-500 text-white rounded-br-md'
                                                        : 'bg-white shadow-sm border border-secondary-200 text-secondary-900 rounded-bl-md'
                                                }`}>
                                                    {msg.body && (
                                                        <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                                                    )}

                                                    {/* MMS media */}
                                                    {msg.media.length > 0 && (
                                                        <div className="mt-1.5 space-y-1.5">
                                                            {msg.media.map(m => (
                                                                <div key={m.id}>
                                                                    {(m.status === 'downloaded' || m.status === 'uploaded') && m.downloadUrl ? (
                                                                        m.contentType?.startsWith('image/') ? (
                                                                            <div className="relative group inline-block">
                                                                                <img
                                                                                    src={m.downloadUrl}
                                                                                    alt="MMS media"
                                                                                    className="max-w-full rounded-lg max-h-64 object-contain cursor-pointer"
                                                                                    onClick={() => setPreviewImageUrl(m.downloadUrl!)}
                                                                                />
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); void handleDownloadMediaUrl(m.downloadUrl!); }}
                                                                                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-black/50 rounded-full text-white transition-opacity"
                                                                                    title="Download"
                                                                                >
                                                                                    <Download className="w-3.5 h-3.5" />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <a
                                                                                href={m.downloadUrl}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className={`flex items-center gap-1.5 text-xs underline ${isSent ? 'text-primary-100' : 'text-primary-600'}`}
                                                                            >
                                                                                <Image className="w-4 h-4" />
                                                                                {m.contentType ?? 'Attachment'}
                                                                            </a>
                                                                        )
                                                                    ) : m.status === 'pending' ? (
                                                                        <span className={`flex items-center gap-1 text-xs ${isSent ? 'text-primary-100' : 'text-secondary-400'}`}>
                                                                            <Loader2 className="w-3 h-3 animate-spin" /> Downloading media…
                                                                        </span>
                                                                    ) : (
                                                                        <span className={`flex items-center gap-1 text-xs ${isSent ? 'text-primary-200' : 'text-error-500'}`}>
                                                                            <AlertCircle className="w-3 h-3" /> Media unavailable
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* MMS badge fallback: mediaCount reported but media array empty */}
                                                    {!msg.body && msg.media.length === 0 && msg.mediaCount > 0 && (
                                                        <p className={`text-xs italic ${isSent ? 'text-primary-100' : 'text-secondary-400'}`}>• MMS</p>
                                                    )}

                                                    {/* Timestamp + status */}
                                                    <div className={`flex items-center flex-wrap gap-1.5 mt-1 ${isSent ? (isError ? 'text-error-500' : 'text-primary-100') : 'text-secondary-400'}`}>
                                                        <span className="text-xs">{formatMessageTime(msg.createdAt)}</span>
                                                        {isSent && (
                                                            <span className="text-xs">
                                                                {isFailed
                                                                    ? '• Failed'
                                                                    : isUndelivered
                                                                        ? '• Undelivered'
                                                                        : isPending
                                                                            ? '• Sending…'
                                                                            : isSentConfirmed
                                                                                ? '• Sent'
                                                                                : msg.status === 'delivered'
                                                                                    ? '• Delivered'
                                                                                    : ''}
                                                            </span>
                                                        )}
                                                        {isSent && showSenderNames && msg.sentByUserName && (
                                                            <span className="text-xs">· {msg.sentByUserName}</span>
                                                        )}
                                                        {msg.jobRef && (
                                                            canOpenJob ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onOpenJob?.(msg.jobId!)}
                                                                    className="inline-flex items-center gap-0.5 text-xs underline-offset-2 hover:underline"
                                                                >
                                                                    · Job {msg.jobRef}
                                                                    <ArrowUpRight className="w-3 h-3" />
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs">· Job {msg.jobRef}</span>
                                                            )
                                                        )}
                                                    </div>

                                                    {/* Delivery error reason (failed / undelivered) */}
                                                    {isError && (msg.errorMessage || msg.errorCode != null) && (
                                                        <div className="mt-1 flex items-start gap-1 text-xs text-error-700">
                                                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                                            <span className="break-words">
                                                                {msg.errorMessage || 'Message could not be delivered'}
                                                                {msg.errorCode != null && ` (${msg.errorCode})`}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {!isSent && jobLinkButton}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}
                </div>
            </div>

            {/* Composer */}
            <div className={`flex-shrink-0 border-t border-secondary-200 bg-white p-4 ${composerClassName ?? ''}`}>
                {/* Send error */}
                {sendError && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg px-3 py-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{sendError}</span>
                        {onClearSendError && (
                            <button onClick={onClearSendError} className="ml-auto text-error-400 hover:text-error-600">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}

                {/* Pending file chips */}
                {pendingFiles.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {pendingFiles.map(f => (
                            <div key={f.objectKey} className="flex items-center gap-1.5 bg-primary-50 border border-primary-200 rounded-lg text-xs text-primary-700 overflow-hidden">
                                {f.previewUrl ? (
                                    <img src={f.previewUrl} alt={f.name} className="h-10 w-10 object-cover flex-shrink-0" />
                                ) : (
                                    <div className="flex items-center justify-center h-10 w-10 flex-shrink-0">
                                        <FileText className="w-4 h-4" />
                                    </div>
                                )}
                                <span className="max-w-[100px] truncate">{f.name}</span>
                                <button
                                    onClick={() => removeFile(f.objectKey)}
                                    className="ml-0.5 mr-1.5 text-primary-400 hover:text-primary-700"
                                    title="Remove"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Upload progress */}
                {isUploading && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-secondary-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Uploading…
                    </div>
                )}

                {/* Upload error */}
                {uploadError && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-error-600 bg-error-50 border border-error-200 rounded-lg px-2.5 py-1.5">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{uploadError}</span>
                        <button onClick={() => setUploadError(null)} className="ml-auto text-error-400 hover:text-error-600">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                )}

                {/* Hidden file input */}
                {supportsMms && (
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={MMS_ACCEPT}
                        className="hidden"
                        onChange={handleFileSelect}
                    />
                )}

                {/* Input row */}
                <div className="flex items-end gap-2">
                    {supportsMms && (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading || pendingFiles.length >= MMS_MAX_FILES}
                            className={`p-2 rounded-lg transition-colors ${pendingFiles.length > 0 ? 'text-primary-600 bg-primary-50' : 'text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100'} disabled:opacity-40`}
                            title="Attach files"
                        >
                            <Paperclip className="w-5 h-5" />
                        </button>
                    )}
                    <textarea
                        ref={textareaRef}
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        enterKeyHint="enter"
                        placeholder={disabled ? (disabledPlaceholder ?? 'Loading…') : placeholder}
                        disabled={!!disabled}
                        rows={1}
                        maxLength={1600}
                        className="flex-1 resize-none px-4 py-3 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 max-h-32 disabled:text-secondary-400"
                        style={{ minHeight: '48px' }}
                    />
                    <button
                        onClick={() => void handleSend()}
                        disabled={!canSend}
                        className="p-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                        aria-label="Send"
                    >
                        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                </div>
                <p className="text-xs text-secondary-400 mt-1 text-right">{messageInput.length}/1600</p>
            </div>

            {/* Image preview modal */}
            <ImagePreviewModal
                isOpen={previewImageUrl !== null}
                onClose={() => setPreviewImageUrl(null)}
                imageUrl={previewImageUrl ?? ''}
                onDownload={handleDownloadPreview}
                isDownloading={isDownloadingMedia}
            />
        </div>
    );
}
